import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  AuditHistoryQuery,
  EnrollmentAuditCategory,
  EnrollmentAuditEntry,
  EnrollmentAuditService,
} from '../../../services/enrollment-audit.service';

type Mode = 'individual' | 'corporate';

@Component({
  selector: 'app-enrollment-audit-timeline',
  templateUrl: './enrollment-audit-timeline.component.html',
})
export class EnrollmentAuditTimelineComponent implements OnChanges {
  @Input() enrollmentId!: number;
  @Input() mode: Mode = 'individual';

  loading = false;
  error: string | null = null;
  entries: EnrollmentAuditEntry[] = [];
  totalCount = 0;
  pageNumber = 1;
  pageSize = 50;

  showAccess = false; // toggle "Mostrar accesos"
  selectedCategory: EnrollmentAuditCategory | null = null;
  expandedEntries = new Set<number>();

  readonly categories: { value: EnrollmentAuditCategory | null; label: string }[] = [
    { value: null, label: 'Todas' },
    { value: EnrollmentAuditCategory.Status, label: 'Estado' },
    { value: EnrollmentAuditCategory.PaymentPlan, label: 'Plan de pagos' },
    { value: EnrollmentAuditCategory.Portfolio, label: 'Cartera' },
    { value: EnrollmentAuditCategory.Income, label: 'Ingresos' },
    { value: EnrollmentAuditCategory.Settings, label: 'Ajustes' },
    { value: EnrollmentAuditCategory.Observation, label: 'Observaciones' },
    { value: EnrollmentAuditCategory.Participants, label: 'Participantes' },
  ];

  constructor(private auditService: EnrollmentAuditService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['enrollmentId'] || changes['mode']) && this.enrollmentId) {
      this.pageNumber = 1;
      this.load();
    }
  }

  load(): void {
    if (!this.enrollmentId) return;
    this.loading = true;
    this.error = null;
    const query: AuditHistoryQuery = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      category: this.selectedCategory,
    };
    const obs = this.mode === 'individual'
      ? this.auditService.getIndividualHistory(this.enrollmentId, query)
      : this.auditService.getCorporateHistory(this.enrollmentId, query);

    obs.subscribe({
      next: (page) => {
        this.entries = page.items ?? [];
        this.totalCount = page.totalCount ?? 0;
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message ?? 'No se pudo cargar el historial';
        this.loading = false;
      },
    });
  }

  onCategoryChange(value: EnrollmentAuditCategory | null): void {
    this.selectedCategory = value;
    this.pageNumber = 1;
    this.load();
  }

  toggleAccess(): void {
    this.showAccess = !this.showAccess;
  }

  visibleEntries(): EnrollmentAuditEntry[] {
    return this.showAccess
      ? this.entries
      : this.entries.filter(e => e.category !== EnrollmentAuditCategory.Access);
  }

  toggleExpand(id: number): void {
    if (this.expandedEntries.has(id)) {
      this.expandedEntries.delete(id);
    } else {
      this.expandedEntries.add(id);
    }
  }

  isExpanded(id: number): boolean {
    return this.expandedEntries.has(id);
  }

  prettyJson(raw?: string | null): string {
    if (!raw) return '';
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }

  iconForCategory(category: number): string {
    switch (category) {
      case EnrollmentAuditCategory.Status: return '🟢';
      case EnrollmentAuditCategory.PaymentPlan: return '📋';
      case EnrollmentAuditCategory.Portfolio: return '💼';
      case EnrollmentAuditCategory.Income: return '💰';
      case EnrollmentAuditCategory.Settings: return '⚙️';
      case EnrollmentAuditCategory.Observation: return '📝';
      case EnrollmentAuditCategory.Participants: return '👥';
      case EnrollmentAuditCategory.Access: return '👁️';
      default: return '•';
    }
  }

  colorForCategory(category: number): string {
    switch (category) {
      case EnrollmentAuditCategory.Status: return 'bg-green-50 border-green-200 text-green-800';
      case EnrollmentAuditCategory.PaymentPlan: return 'bg-blue-50 border-blue-200 text-blue-800';
      case EnrollmentAuditCategory.Portfolio: return 'bg-amber-50 border-amber-200 text-amber-800';
      case EnrollmentAuditCategory.Income: return 'bg-emerald-50 border-emerald-200 text-emerald-800';
      case EnrollmentAuditCategory.Settings: return 'bg-purple-50 border-purple-200 text-purple-800';
      case EnrollmentAuditCategory.Observation: return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case EnrollmentAuditCategory.Participants: return 'bg-pink-50 border-pink-200 text-pink-800';
      case EnrollmentAuditCategory.Access: return 'bg-gray-50 border-gray-200 text-gray-700';
      default: return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  prevPage(): void {
    if (this.pageNumber > 1) { this.pageNumber--; this.load(); }
  }
  nextPage(): void {
    if (this.pageNumber < this.totalPages) { this.pageNumber++; this.load(); }
  }
}
